import {
  AccountProvider,
  LoginInfo,
  isAuthToken,
  LogInCredentials,
  isLoginCredentials,
} from "../interfaces/AccountProvider";
import {
  ClientLogoutResult,
  Core,
  ResponseErrors,
  UserAccountInfoGetResult,
  isUserLoginCheckResult,
  UserLoginStatus,
} from "./core";
import { SorensonAccountService } from "./SorensonAccountService";
import AsyncStorage from "@react-native-async-storage/async-storage";

export class SorensonAccountProvider implements AccountProvider {
  public get mac() {
    return this._mac;
  }
  public get userId() {
    return this._userId;
  }

  constructor(private _mac?: string, private _userId?: string) {}

  async getAnonymousAccount(): Promise<SorensonAccountService> {
    throw new Error("Method not implemented.");
  }

  async logIn(loginInfo: LoginInfo): Promise<SorensonAccountService> {
    // Get or generate MAC first.
    await this.getMac();
    if (!this.mac) {
      return Promise.reject("Could not get or generate MAC");
    }
    if (isLoginCredentials(loginInfo)) {
      try {
        // First check to see if core recognizes our MAC address
        const macCheckResponse = await Core.getInstance().updateVersionCheck({
          UpdateVersionCheck: {
            UniqueId: this.mac,
            AppVersion: "0.1",
            AppLoaderVersion: "",
          },
        });
        if (macCheckResponse.UpdateVersionCheckResult.$.Result === "Error") {
          if (
            macCheckResponse.UpdateVersionCheckResult.$.ErrorNum ===
            ResponseErrors.UniqueIDNotRecognized
          ) {
            // We need to create a phone account so that core will recognize our mac/unique id
            const phoneAccountCreateResponse =
              await Core.getInstance().phoneAccountCreate({
                PhoneAccountCreate: {
                  PhoneIdentity: {
                    UniqueId: this.mac,
                    PhoneType: "NTOUCH-PC",
                  },
                },
              });
            if (
              phoneAccountCreateResponse.PhoneAccountCreateResult.$.Result ===
              "OK"
            ) {
              console.log("Phone Account Created");
            } else {
              return Promise.reject("Failed to create phone account");
            }
          }
        }
        const clientAuthenticateResult =
          await Core.getInstance().clientAuthenticate([
            {
              UserLoginCheck: {
                PhoneIdentity: {
                  UniqueId: this.mac,
                },
                UserIdentity: {
                  PhoneNumber: loginInfo.username,
                  Pin: loginInfo.password,
                },
              },
            },
            {
              ClientAuthenticate: {
                PhoneIdentity: {
                  UniqueId: this.mac,
                  RingGroupCapable: true,
                },
                UserIdentity: {
                  PhoneNumber: loginInfo.username,
                  Pin: loginInfo.password,
                },
              },
            },
          ]);
        if (
          clientAuthenticateResult.ClientAuthenticateResult.$.Result === "Error"
        ) {
          const num =
            clientAuthenticateResult.ClientAuthenticateResult.$.ErrorNum;
          const msg =
            clientAuthenticateResult.ClientAuthenticateResult.$.ErrorMsg;
          return Promise.reject(
            new Error(`ErrorNum: ${num}; ErrorMsg: ${msg}`)
          );
        }
        const token =
          clientAuthenticateResult.ClientAuthenticateResult.ClientToken;
        const userId =
          clientAuthenticateResult.ClientAuthenticateResult.PhoneUserID;
        const accountService = new SorensonAccountService();
        accountService.authToken = token;
        Core.getInstance().authToken = token;
        this._userId = userId;
        console.table({ mac: this.mac, token, userId });
        return Promise.resolve(accountService);
      } catch (error) {
        return Promise.reject(error);
      }
    } else if (isAuthToken(loginInfo)) {
      return Promise.reject(new Error("Login from token not implemented yet"));
    }
    return Promise.reject(
      new Error("Could not determine login parameter type")
    );
  }

  async getMac(): Promise<string> {
    const storeKey = "mac_store_key";
    try {
      const storedMac = await AsyncStorage.getItem(storeKey);
      if (storedMac !== null) {
        this._mac = storedMac;
        return Promise.resolve(storedMac);
      }
      const newMac = this.generateMacAddress();
      await AsyncStorage.setItem(storeKey, newMac);
      this._mac = newMac;
      return Promise.resolve(newMac);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async logout(): Promise<boolean> {
    try {
      const requestBody = {
        ClientLogout: {
          PhoneIdentity: {
            UniqueId: await this.getMac(),
          },
        },
      };
      const clientAuthenticateResult = (await Core.getInstance().coreRequest(
        requestBody
      )) as ClientLogoutResult;
      if (clientAuthenticateResult.ClientLogoutResult.$.Result === "OK") {
        return Promise.resolve(true);
      }
      return Promise.reject(
        `Error while logging out: ${clientAuthenticateResult.ClientLogoutResult.$.ErrorMsg}`
      );
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async userLoginCheck({ username, password }: LogInCredentials): Promise<{
    loginStatus: UserLoginStatus;
    statusMessage: string;
  }> {
    const coreResponse = await Core.getInstance().coreRequest({
      UserLoginCheck: {
        PhoneIdentity: {
          UniqueId: await this.getMac(),
        },
        UserIdentity: {
          PhoneNumber: username,
          Pin: password,
        },
      },
    });
    if (
      isUserLoginCheckResult(coreResponse) &&
      coreResponse.UserLoginCheckResult.$.ResultNumber !== undefined &&
      coreResponse.UserLoginCheckResult.$.ResultMsg
    ) {
      const statusMessage = coreResponse.UserLoginCheckResult.$.ResultMsg;
      const statusNumber = coreResponse.UserLoginCheckResult.$.ResultNumber;
      const loginStatus = statusNumber as UserLoginStatus;
      return { loginStatus, statusMessage };
    } else {
      return Promise.reject(coreResponse);
    }
  }

  generateMacAddress() {
    return "FF:05:XX:XX:XX:XX".replace(/X/g, () => {
      return "0123456789ABCDEF".charAt(Math.floor(Math.random() * 16));
    });
  }

  async getUserInfo(): Promise<UserAccountInfoGetResult> {
    try {
      let requestBody = {
        UserAccountInfoGet: {},
      };
      const userInfo = (await Core.getInstance().coreRequest(
        requestBody
      )) as UserAccountInfoGetResult;
      if (userInfo.UserAccountInfoGetResult.$.Result === "OK") {
        return Promise.resolve(userInfo);
      }
      return Promise.reject("Error while getting user info");
    } catch (e) {
      return Promise.reject("Error while getting user info");
    }
  }
}
